import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Toast notification type
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Toast notification interface
 */
export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  dismissible?: boolean;
  timestamp: Date;
}

/**
 * Toast notification service
 * Provides user-friendly notifications throughout the application
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastsSubject = new BehaviorSubject<Toast[]>([]);
  public toasts$: Observable<Toast[]> = this.toastsSubject.asObservable();

  private readonly DEFAULT_DURATION = 5000;
  private toastCounter = 0;
  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Show a success toast
   */
  success(message: string, title?: string, duration?: number): void {
    this.show({
      type: 'success',
      title: title || 'Success',
      message,
      duration: duration || this.DEFAULT_DURATION,
      dismissible: true
    });
  }

  /**
   * Show an error toast
   */
  showError(message: string, title?: string, duration?: number): void {
    this.show({
      type: 'error',
      title: title || 'Error',
      message,
      duration: duration || this.DEFAULT_DURATION * 2, // Errors stay longer
      dismissible: true
    });
  }

  error(message: string, title?: string, duration?: number): void {
    this.showError(message, title, duration);
  }

  /**
   * Show a warning toast
   */
  warning(message: string, title?: string, duration?: number): void {
    this.show({
      type: 'warning',
      title: title || 'Warning',
      message,
      duration: duration || this.DEFAULT_DURATION,
      dismissible: true
    });
  }

  /**
   * Show an info toast
   */
  info(message: string, title?: string, duration?: number): void {
    this.show({
      type: 'info',
      title: title || 'Information',
      message,
      duration: duration || this.DEFAULT_DURATION,
      dismissible: true
    });
  }

  /**
   * Show a custom toast
   */
  show(toast: Omit<Toast, 'id' | 'timestamp'>): void {
    const newToast: Toast = {
      ...toast,
      id: `toast-${++this.toastCounter}-${Date.now()}`,
      timestamp: new Date()
    };

    const currentToasts = this.toastsSubject.value;
    this.toastsSubject.next([...currentToasts, newToast]);

    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        this.timers.delete(newToast.id);
        this.dismiss(newToast.id);
      }, toast.duration);
      this.timers.set(newToast.id, timer);
    }
  }

  /**
   * Dismiss a toast by ID
   */
  dismiss(id: string): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    this.toastsSubject.next(this.toastsSubject.value.filter(t => t.id !== id));
  }

  /**
   * Dismiss all toasts
   */
  dismissAll(): void {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
    this.toastsSubject.next([]);
  }

  /**
   * Get current toasts
   */
  getToasts(): Toast[] {
    return this.toastsSubject.value;
  }
}
